import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { ListFilter } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ColumnFilterConfig, FiltersState } from './DataTable';

interface ColumnFilterProps {
  columnId: string;
  config: ColumnFilterConfig;
  filters: FiltersState;
  onFilterChange: (filters: FiltersState) => void;
}

export const ColumnFilter = ({ columnId, config, filters, onFilterChange }: ColumnFilterProps) => {
  const filterValue = filters[columnId];
  const isActive = !!filterValue;

  let activeCount = 0;
  if (filterValue?.type === 'options') {
    activeCount = filterValue.values.length;
  } else if (filterValue?.type === 'text' && filterValue.value) {
    activeCount = 1;
  }

  const handleOptionsChange = (values: string[]) => {
    const next = { ...filters };
    if (values.length === 0) {
      delete next[columnId];
    } else {
      next[columnId] = { type: 'options', values };
    }
    onFilterChange(next);
  };

  const handleTextChange = (value: string) => {
    const next = { ...filters };
    if (!value) {
      delete next[columnId];
    } else {
      next[columnId] = { type: 'text', value };
    }
    onFilterChange(next);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'inline-flex items-center gap-0.5 p-1.5 rounded-md hover:bg-accent/50 cursor-pointer relative',
            isActive && 'text-primary',
          )}>
          <ListFilter className='size-3.5' />
          {activeCount > 0 && (
            <span className='inline-flex absolute -top-1 -right-1.5 items-center justify-center size-4 rounded-full bg-primary text-primary-foreground text-[10px] font-medium leading-none'>
              {activeCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align='start' className='w-56 p-1'>
        {config.type === 'options' && (
          <OptionsFilterContent
            options={config.options}
            selectedValues={filterValue?.type === 'options' ? filterValue.values : []}
            onChange={handleOptionsChange}
          />
        )}
        {config.type === 'text' && (
          <TextFilterContent
            value={filterValue?.type === 'text' ? filterValue.value : ''}
            onChange={handleTextChange}
          />
        )}
      </PopoverContent>
    </Popover>
  );
};

interface OptionsFilterContentProps {
  options: { label: string; value: string }[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
}

const OptionsFilterContent = ({ options, selectedValues, onChange }: OptionsFilterContentProps) => {
  const { t } = useTranslation('components');
  return (
    <div className='flex flex-col'>
      <div className='max-h-60 overflow-y-auto flex flex-col gap-0.5'>
        {options.map((option) => {
          const isChecked = selectedValues.includes(option.value);
          return (
            <label
              key={option.value}
              className='flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer text-sm'>
              <Checkbox
                checked={isChecked}
                onCheckedChange={(checked) => {
                  if (checked) {
                    onChange([...selectedValues, option.value]);
                  } else {
                    onChange(selectedValues.filter((v) => v !== option.value));
                  }
                }}
              />
              {option.label}
            </label>
          );
        })}
      </div>
      {selectedValues.length > 0 && (
        <button
          className='w-full text-xs text-muted-foreground mt-2 py-1 hover:text-foreground cursor-pointer'
          onClick={() => onChange([])}>
          {t('dataTableFilter.clearAll')}
        </button>
      )}
    </div>
  );
};

interface TextFilterContentProps {
  value: string;
  onChange: (value: string) => void;
}

const TextFilterContent = ({ value, onChange }: TextFilterContentProps) => {
  const { t } = useTranslation('components');
  return (
    <div className='flex flex-col'>
      <Input autoFocus placeholder={t('dataTableFilter.filter')} value={value} onChange={(e) => onChange(e.target.value)} />
      {value && (
        <button
          className='w-full text-xs text-muted-foreground mt-2 py-1 hover:text-foreground cursor-pointer'
          onClick={() => onChange('')}>
          {t('dataTableFilter.clear')}
        </button>
      )}
    </div>
  );
};
